<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import OmniCombobox from "@/components/selects/OmniCombobox.vue";
import { useValidation } from "@/composables/useValidation";
import { OrderStopTypeMap } from "@/constants/order";
import type { Bol, RouteStopDto, TravelOrderOrderInfo } from "@/api/trips/dto/trip.dto";
import type { TripRouteStop } from "@/types/trip";
import { generateId } from "@/utils/generateId";
import { useAppStore } from "@/store/app.store";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";

const { t } = useI18n();
const { requiredValidator } = useValidation();
const { dispatcher } = useAppStore();

const model = defineModel<Bol | null>({ default: null });

const emit = defineEmits<{
  (e: "delete"): void;
}>();

const props = withDefaults(
  defineProps<{
    reusedBols: Bol[];
    routeStop: TripRouteStop | RouteStopDto;
    routeStops: (TripRouteStop | RouteStopDto)[];
    disabled?: boolean;
    readonly?: boolean;
    required?: boolean;
    errorMessage?: string | null;
    order?: TravelOrderOrderInfo | null;
  }>(),
  {
    disabled: false,
    readonly: false,
    required: false,
    errorMessage: null,
    order: null,
  },
);

const isBolFromAnotherStop = computed<boolean>(() => {
  if (!model.value) {
    return false;
  }

  return model.value.routeStopId !== props.routeStop.id;
});

const isDeleteDisabled = computed<boolean>(() => {
  return props.disabled || props.readonly || isBolFromAnotherStop.value;
});

const bolRouteStopName = computed<string | null>(() => {
  const bolRouteStop = props.routeStops.find((stop) => stop.id && stop.id === model.value?.routeStopId);

  return bolRouteStop ? `${OrderStopTypeMap[bolRouteStop.type]} #${bolRouteStop.number}` : null;
});

const bolReusedMessage = computed<string>(() => {
  if (!bolRouteStopName.value) {
    return t("trip.bol-reused-at-another-stop", { number: model.value?.number });
  }

  return t("trip.bol-reused-at-stop", { number: model.value?.number, stop: bolRouteStopName.value });
});

const bolNumber = computed<string | null>({
  get() {
    return model.value?.number ?? null;
  },
  set(number) {
    model.value = createBol(number);
  },
});

function createBol(number: string | null): Bol | null {
  if (!number?.trim()) {
    return null;
  }

  const savedBol = props.reusedBols.find((bol) => bol.number === number);

  if (savedBol) {
    return savedBol;
  }

  if (model.value && !isBolFromAnotherStop.value) {
    return { ...model.value, number };
  }

  const newBol = {
    number,
    id: generateId(),
    routeStopId: props.routeStop.id,
    orderId: props.order?.id,
    signedBy: {},
  };

  return newBol;
}
</script>

<template>
  <div class="d-flex align-center col-gap-2 mb-4">
    <omni-combobox
      v-model="bolNumber"
      :disabled="props.disabled || props.readonly"
      :error-messages="props.errorMessage ? [props.errorMessage] : []"
      :items="props.reusedBols"
      :label="t('trip.bol-number')"
      :readonly="isBolFromAnotherStop"
      :required="props.required"
      :rules="props.required ? [requiredValidator] : []"
      always-filter
      class="flex-grow-1"
      data-qa="bol-number-field"
      item-title="number"
      item-value="number"
    >
      <template v-if="props.reusedBols.length && props.order" #prepend-item>
        <div class="px-4 py-2">
          {{ t("trip.reuse-bol-from-order", { number: props.order.number }) }}
        </div>
      </template>
    </omni-combobox>

    <omni-tooltip :disabled="!isBolFromAnotherStop">
      <template #activator>
        <v-btn
          :disabled="isDeleteDisabled"
          color="error"
          data-qa="delete-bol-button"
          icon="mdi-delete-outline"
          variant="text"
          @click="emit('delete')"
        />
      </template>

      {{ bolReusedMessage }}
    </omni-tooltip>
  </div>
</template>

<style lang="scss" scoped>
.omni-combobox:deep(.v-field__clearable) {
  pointer-events: auto;
}
</style>
